import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';

const TeamNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/team/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/team/profile', label: 'Profile', icon: '👥' },
    { path: '/team/leaderboards', label: 'Leaderboards', icon: '🏆' },
    { path: '/team/feed', label: 'Feed', icon: '📱' },
    { path: '/team/swag', label: 'Swag Store', icon: '🛍️' },
    { path: '/venue-discovery', label: 'Find Games', icon: '🎯' },
    { path: '/team/help', label: 'Help', icon: '❓' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <nav className="bg-gray-900 border-b border-gray-700 sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Empty space where logo was */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {/* Logo removed */}
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="flex items-center space-x-2">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`px-6 py-3 rounded-xl text-base font-semibold transition-all duration-200 flex items-center space-x-2 ${
                    isActive(item.path)
                      ? 'bg-green-600 text-white shadow-lg shadow-green-600/25'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="px-6 py-3 rounded-xl text-base font-semibold transition-all duration-200 flex items-center space-x-2 text-gray-300 hover:bg-red-600 hover:text-white"
              >
                <span className="text-lg">🚪</span>
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Tablet Navigation (fewer items) */}
          <div className="hidden md:block lg:hidden">
            <div className="flex items-center space-x-2">
              {navItems.slice(0, 4).map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center space-x-2 ${
                    isActive(item.path)
                      ? 'bg-green-600 text-white shadow-lg shadow-green-600/25'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center space-x-2 text-gray-300 hover:bg-red-600 hover:text-white"
              >
                <span className="text-base">🚪</span>
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="bg-gray-800 inline-flex items-center justify-center p-3 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-green-500 transition-all duration-200"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden" id="mobile-menu">
          <div className="px-4 pt-4 pb-6 space-y-2 bg-gray-800 border-t border-gray-700 shadow-lg">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-6 py-4 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center space-x-3 ${
                  isActive(item.path)
                    ? 'bg-green-600 text-white shadow-lg shadow-green-600/25'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white bg-gray-800'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
            
            {/* Logout Button */}
            <button
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-6 py-4 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center space-x-3 text-gray-300 hover:bg-red-600 hover:text-white bg-gray-800"
            >
              <span className="text-xl">🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default TeamNavbar; 